import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import {
  PlanPermissionsService,
  UserPermissions,
  MARKETS_BY_TIER,
} from '../services/planPermissions';
import { SubscriptionService, TrialEligibility, TrialStatus } from '../services/subscriptionService';

// Evento personalizado para notificar cambios en permisos
const PERMISSIONS_UPDATED_EVENT = 'userPermissionsUpdated';

/**
 * Hook para obtener y manejar los permisos del plan del usuario
 */
export function useUserPlan() {
  const { isAuthenticated, user } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastUserIdRef = useRef<string | null>(null);
  const [trialStatus, setTrialStatus] = useState<TrialStatus>({ is_in_trial: false });
  const [trialEligibility, setTrialEligibility] = useState<TrialEligibility>({ eligible: false });

  // Cargar permisos cuando cambia el usuario - OPTIMIZADO para evitar re-renders y recargas innecesarias
  useEffect(() => {
    let cancelled = false;

    async function loadPermissions() {
      if (cancelled) return;

      const currentUserId = user?.id || null;

      // Si no está autenticado
      if (!isAuthenticated) {
        // Si ya tenemos permisos cargados, NO hacer nada (podría ser oscilación temporal)
        if (permissions) {
          setLoading(false);
          return;
        }

        // Solo establecer permisos guest si es la primera carga
        if (!cancelled) {
          setPermissions({
            plan: 'guest',
            analyses_today: 0,
            max_analyses: 0,
            analyses_remaining: 0,
            max_leagues: 0,
            max_markets: 3,
            can_view_ai_insights: false,
            can_view_value_picks: false,
            can_view_personalized: false,
            has_priority_support: false,
            has_early_access: false,
            favorite_leagues: [],
          });
          setLoading(false);
        }
        return;
      }

      // Usuario autenticado - verificar si es el mismo usuario
      if (currentUserId === lastUserIdRef.current && permissions) {
        setLoading(false);
        return;
      }

      // Usuario nuevo o primera carga - cargar permisos
      setLoading(true);
      setError(null);

      try {
        const [perms, tStatus, tEligibility] = await Promise.all([
          PlanPermissionsService.getUserPermissions(),
          SubscriptionService.getTrialStatus(),
          SubscriptionService.getTrialEligibility(),
        ]);
        if (!cancelled) {
          setPermissions(perms);
          setTrialStatus(tStatus);
          setTrialEligibility(tEligibility);
          lastUserIdRef.current = currentUserId;
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Error al cargar permisos');
          setLoading(false);
        }
      }
    }

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]); // Depender de user.id para detectar cambios reales de usuario

  // Escuchar eventos de actualización de permisos de otras instancias del hook
  useEffect(() => {
    const handlePermissionsUpdate = async () => {
      if (isAuthenticated) {
        try {
          const perms = await PlanPermissionsService.getUserPermissions();
          setPermissions(perms);
        } catch (err) {
          // silently ignore
        }
      }
    };

    window.addEventListener(PERMISSIONS_UPDATED_EVENT, handlePermissionsUpdate);

    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, handlePermissionsUpdate);
    };
  }, [isAuthenticated]);

  /**
   * Refrescar permisos (después de un upgrade o acción)
   */
  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const [perms, tStatus, tEligibility] = await Promise.all([
        PlanPermissionsService.getUserPermissions(),
        SubscriptionService.getTrialStatus(),
        SubscriptionService.getTrialEligibility(),
      ]);
      setPermissions(perms);
      setTrialStatus(tStatus);
      setTrialEligibility(tEligibility);
      window.dispatchEvent(new CustomEvent(PERMISSIONS_UPDATED_EVENT));
    } catch (err) {
    }
  }, [isAuthenticated]);

  /**
   * Registrar vista de análisis - OPTIMIZADO sin dependencia de permissions
   */
  const trackAnalysisView = useCallback(async (fixtureId: number) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const result = await PlanPermissionsService.incrementAnalysisCount(fixtureId);
      if (result.success || result.already_viewed) {
        // Actualizar permisos localmente de forma optimista
        setPermissions(prev => {
          if (!prev) return null;

          const newAnalysesToday = result.current_count || (prev.analyses_today + (result.already_viewed ? 0 : 1));
          const newAnalysesRemaining = Math.max(0, prev.max_analyses - newAnalysesToday);

          return {
            ...prev,
            analyses_today: newAnalysesToday,
            analyses_remaining: newAnalysesRemaining,
          };
        });

        // Notificar a otras instancias del hook sobre la actualización
        window.dispatchEvent(new CustomEvent(PERMISSIONS_UPDATED_EVENT));
      }

      return result;
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }, [isAuthenticated]); // Solo depender de isAuthenticated

  /**
   * Verificar acceso a una liga específica
   */
  const canAccessLeague = useCallback(async (leagueId: number): Promise<boolean> => {
    if (!permissions) return false;

    // Pro, Experto y Free tienen acceso a todas las ligas
    if (permissions.plan === 'pro' || permissions.plan === 'experto' || permissions.plan === 'free') {
      return true;
    }

    // Standard: verificar favoritos
    if (permissions.plan === 'standard') {
      return permissions.favorite_leagues.includes(leagueId);
    }

    return false;
  }, [permissions]);

  /**
   * Agregar liga favorita
   */
  const addFavoriteLeague = useCallback(async (leagueId: number) => {
    const result = await PlanPermissionsService.addFavoriteLeague(leagueId);

    if (result.success) {
      await refreshPermissions();
    }

    return result;
  }, [refreshPermissions]);

  /**
   * Remover liga favorita
   */
  const removeFavoriteLeague = useCallback(async (leagueId: number) => {
    const result = await PlanPermissionsService.removeFavoriteLeague(leagueId);

    if (result.success) {
      await refreshPermissions();
    }

    return result;
  }, [refreshPermissions]);

  /**
   * Obtener mercados disponibles para el plan actual
   */
  const getAvailableMarkets = useCallback(() => {
    if (!permissions) return MARKETS_BY_TIER.basic.slice(0, 3);
    return PlanPermissionsService.getAvailableMarkets(permissions.plan);
  }, [permissions]);

  /**
   * Verificar si un mercado específico está disponible
   */
  const isMarketAvailable = useCallback((marketKey: string) => {
    if (!permissions) return false;
    return PlanPermissionsService.isMarketAvailable(permissions.plan, marketKey);
  }, [permissions]);

  /**
   * Verificar si puede ver más análisis
   */
  const canViewMoreAnalyses = useCallback(() => {
    if (!permissions) return false;
    return permissions.analyses_remaining > 0;
  }, [permissions]);

  /**
   * Obtener mensaje de upgrade
   */
  const getUpgradeMessage = useCallback((limitType: 'analyses' | 'leagues' | 'markets') => {
    if (!permissions) return '';
    return PlanPermissionsService.getUpgradeMessage(limitType, permissions.plan);
  }, [permissions]);

  /**
   * Verificar si el usuario tiene un plan premium (no free ni guest)
   */
  const isPremium = permissions?.plan && !['free', 'guest'].includes(permissions.plan);

  /**
   * Verificar si el usuario es Experto
   */
  const isExperto = permissions?.plan === 'experto';

  /**
   * Verificar si el usuario es Pro o superior
   */
  const isProOrHigher = permissions?.plan === 'pro' || permissions?.plan === 'experto';

  return {
    // Estado
    permissions,
    loading: loading || (isAuthenticated && lastUserIdRef.current !== (user?.id || null)),
    error,
    plan: permissions?.plan || 'guest',

    // Trial
    isInTrial: trialStatus.is_in_trial,
    trialPlanCode: trialStatus.plan_code,
    trialEndsAt: trialStatus.trial_ends_at,
    trialDaysRemaining: trialStatus.days_remaining,
    canStartTrial: trialEligibility.eligible,
    trialIneligibleReason: trialEligibility.reason,

    // Flags de conveniencia
    isPremium,
    isExperto,
    isProOrHigher,
    isAuthenticated,

    // Límites
    analysesToday: permissions?.analyses_today || 0,
    maxAnalyses: permissions?.max_analyses || 0,
    analysesRemaining: permissions?.analyses_remaining || 0,
    maxMarkets: permissions?.max_markets || 0,
    favoriteLeagues: permissions?.favorite_leagues || [],

    // Permisos específicos
    canViewAiInsights: permissions?.can_view_ai_insights || false,
    canViewValuePicks: permissions?.can_view_value_picks || false,
    canViewPersonalized: permissions?.can_view_personalized || false,
    hasPrioritySupport: permissions?.has_priority_support || false,
    hasEarlyAccess: permissions?.has_early_access || false,

    // Acciones
    refreshPermissions,
    trackAnalysisView,
    canAccessLeague,
    addFavoriteLeague,
    removeFavoriteLeague,
    getAvailableMarkets,
    isMarketAvailable,
    canViewMoreAnalyses,
    getUpgradeMessage,
  };
}

/**
 * Hook para verificar si se puede acceder a un fixture específico
 */
export function useCanAccessFixture(fixtureId: number, leagueId: number) {
  const { permissions, canAccessLeague, trackAnalysisView, canViewMoreAnalyses } = useUserPlan();
  const [canAccess, setCanAccess] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const [limitReached, setLimitReached] = useState(false);
  const [leagueBlocked, setLeagueBlocked] = useState(false);

  useEffect(() => {
    async function checkAccess() {
      if (!permissions) {
        setChecking(true);
        return;
      }

      setChecking(true);

      // Verificar acceso a la liga
      const hasLeagueAccess = await canAccessLeague(leagueId);

      if (!hasLeagueAccess) {
        setCanAccess(false);
        setLeagueBlocked(true);
        setChecking(false);
        return;
      }

      // Verificar límite de análisis
      if (!canViewMoreAnalyses()) {
        setCanAccess(false);
        setLimitReached(true);
        setChecking(false);
        return;
      }

      setCanAccess(true);
      setChecking(false);
    }

    checkAccess();
  }, [permissions, fixtureId, leagueId, canAccessLeague, canViewMoreAnalyses]);

  /**
   * Registrar la vista cuando el usuario accede
   */
  const registerView = useCallback(async () => {
    return await trackAnalysisView(fixtureId);
  }, [fixtureId, trackAnalysisView]);

  return {
    canAccess,
    checking,
    limitReached,
    leagueBlocked,
    registerView,
  };
}

export type { UserPermissions };
